import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Brain, Check, Settings, Sparkles } from 'lucide-react'
import { cn } from '../../lib/utils'
import { createNadDebug } from '../../utils/debugLog'
import {
  getDirectorModel,
  getDirectorModels,
  getGatewayConfigs,
  setDirectorModel,
  setDirectorModelPreference,
  type DirectorModelInfo,
  type GatewayConfig,
} from '../../api/client'
import GatewaySettingsModal, { type DirectorModelPreset } from './GatewaySettingsModal'
import type { ByomEntryPoint } from '../../analytics/generated/analyticsEvents'
import { trackInteraction } from '../../analytics/interactionAnalytics'
import { modelSourceOf } from '../../analytics/nodeAnalytics'

const GPT_MODEL_ID = 'gpt'
const MUSE_SPARK_MODEL_ID = 'muse-spark'
const BYOM_MODEL_PREFIX = 'gateway/byom/'
const debugDirectorRouting = createNadDebug('dw:director-model-routing:settings')
const DIRECTOR_PRESETS: Record<string, DirectorModelPreset> = {
  [GPT_MODEL_ID]: {
    keyName: 'GPT',
    baseUrl: 'https://api.openai.com/v1',
    providerModel: 'gpt-4.1',
    directorProvider: 'openai_chat_completions',
  },
  [MUSE_SPARK_MODEL_ID]: {
    keyName: 'Muse Spark',
    baseUrl: 'https://api.meta.ai/v1',
    providerModel: 'muse-spark-1.1',
    directorProvider: 'anthropic_messages',
  },
}

interface DirectorModelSettingsSectionProps {
  showTitle?: boolean
  onVisibilityChange?: (visible: boolean) => void
  /** Which BYOM surface hosts this section, for `byom_action` (RFC-1577). */
  analyticsEntryPoint: ByomEntryPoint
}

function isConfigurableDirectorModel(modelId: string): boolean {
  return Object.prototype.hasOwnProperty.call(DIRECTOR_PRESETS, modelId)
}

function isLegacyByomSelection(model: DirectorModelInfo, selectedModel: string): boolean {
  return isConfigurableDirectorModel(model.id) &&
    !!model.config_id &&
    selectedModel === `${BYOM_MODEL_PREFIX}${model.config_id}`
}

export default function DirectorModelSettingsSection({
  showTitle = true,
  onVisibilityChange,
  analyticsEntryPoint,
}: DirectorModelSettingsSectionProps) {
  const { t } = useTranslation('settings')
  const [models, setModels] = useState<DirectorModelInfo[]>([])
  const [gatewayConfigs, setGatewayConfigs] = useState<GatewayConfig[]>([])
  const [selectedModel, setSelectedModelState] = useState(getDirectorModel())
  const [hasLoaded, setHasLoaded] = useState(false)
  const [isVisible, setIsVisible] = useState(false)
  const [configureModelId, setConfigureModelId] = useState<string | null>(null)
  const [selectionError, setSelectionError] = useState<string | null>(null)

  const cacheSelectedModel = useCallback((modelId: string) => {
    setDirectorModel(modelId)
    setSelectedModelState(modelId)
  }, [])

  const fetchModels = useCallback(async () => {
    try {
      const response = await getDirectorModels()
      if (!response.allow_internal_models) {
        setModels([])
        setGatewayConfigs([])
        cacheSelectedModel(response.selected_model ?? response.default_model)
        setSelectionError(null)
        setIsVisible(false)
        return
      }

      let configs: GatewayConfig[] = []
      try {
        configs = await getGatewayConfigs()
      } catch {
        configs = []
      }

      setModels(response.models)
      setGatewayConfigs(configs)
      setIsVisible(true)

      const modelIds = new Set(response.models.map((model) => model.id))
      const currentModel = response.selected_model ?? getDirectorModel()
      const legacySelection = response.models.find((model) => isLegacyByomSelection(model, currentModel))
      const normalizedCurrent = legacySelection
        ? legacySelection.id
        : currentModel
      const nextSelected = modelIds.has(normalizedCurrent)
        ? normalizedCurrent
        : response.default_model
      debugDirectorRouting({
        event: 'settings_models_loaded',
        allow_internal_models: response.allow_internal_models,
        selected_model: nextSelected,
        server_selected_model: currentModel,
        model_count: response.models.length,
      })
      cacheSelectedModel(nextSelected)
    } catch {
      setModels([])
      setGatewayConfigs([])
      setIsVisible(false)
    } finally {
      setHasLoaded(true)
    }
  }, [cacheSelectedModel])

  useEffect(() => {
    fetchModels()
  }, [fetchModels])

  useEffect(() => {
    onVisibilityChange?.(hasLoaded && isVisible)
  }, [hasLoaded, isVisible, onVisibilityChange])

  const configuringModel = useMemo(
    () => models.find((model) => model.id === configureModelId) ?? null,
    [models, configureModelId],
  )
  const configuringConfig = useMemo(
    () => gatewayConfigs.find((config) => config.id === configuringModel?.config_id) ?? null,
    [gatewayConfigs, configuringModel?.config_id],
  )
  const configuringPreset = useMemo(
    () => configureModelId ? DIRECTOR_PRESETS[configureModelId] : undefined,
    [configureModelId],
  )

  const handleConfigureSaved = useCallback(() => {
    setConfigureModelId(null)
    setSelectionError(null)
    fetchModels()
  }, [fetchModels])

  const handleConfigure = useCallback((model: DirectorModelInfo) => {
    if (model.config_id && !gatewayConfigs.some((config) => config.id === model.config_id)) {
      fetchModels()
      return
    }
    setConfigureModelId(model.id)
  }, [fetchModels, gatewayConfigs])

  const handleSelectModel = useCallback(async (modelId: string) => {
    setSelectionError(null)
    try {
      debugDirectorRouting({
        event: 'settings_selection_requested',
        selected_model: modelId,
      })
      trackInteraction('settings_director_model_changed', { model_source: modelSourceOf(modelId) })
      const response = await setDirectorModelPreference(modelId)
      debugDirectorRouting({
        event: 'settings_selection_saved',
        selected_model: response.selected_model,
      })
      cacheSelectedModel(response.selected_model)
    } catch (err) {
      debugDirectorRouting({
        event: 'settings_selection_failed',
        selected_model: modelId,
      })
      const fallbackMessage = t('directorModelSection.selectionFailed')
      setSelectionError(
        err instanceof Error && err.message
          ? `${fallbackMessage}: ${err.message}`
          : fallbackMessage,
      )
      fetchModels()
    }
  }, [cacheSelectedModel, fetchModels, t])

  const hasUnresolvedConfig = Boolean(configuringModel?.config_id && !configuringConfig)

  if (!hasLoaded || !isVisible) {
    return null
  }

  return (
    <div data-testid="director-model-settings-section">
      {showTitle && (
        <h3 className="mb-1 text-xxs font-normal uppercase tracking-wider text-muted-foreground">
          {t('directorModelSection.title')}
        </h3>
      )}

      <div className="space-y-1">
        {selectionError && (
          <div
            role="alert"
            data-testid="director-model-selection-error"
            className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs font-medium text-destructive"
          >
            {selectionError}
          </div>
        )}
        {models.map((model) => {
          const isSelected = model.id === selectedModel ||
            isLegacyByomSelection(model, selectedModel)
          const Icon = model.source === 'byom' ? Sparkles : Brain
          const canConfigure = isConfigurableDirectorModel(model.id)
          return (
            <div
              key={model.id}
              className={cn(
                'flex min-w-0 min-h-11 flex-wrap items-stretch gap-1 rounded-lg border transition-colors',
                model.enabled
                  ? 'border-input-border bg-surface-1 hover:border-input-border-hover hover:bg-surface-3'
                  : 'border-border-subtle bg-input-bg opacity-90',
                isSelected && 'border-primary/50 bg-primary/10',
              )}
              data-testid={`director-model-row-${model.id}`}
            >
              <button
                type="button"
                disabled={!model.enabled}
                onClick={() => { void handleSelectModel(model.id) }}
                className={cn(
                  'flex min-w-0 flex-1 flex-wrap items-center justify-between gap-2 rounded-lg px-2 py-2 text-left',
                  'focus:outline-none focus:ring-2 focus:ring-primary/20',
                  model.enabled ? 'cursor-pointer' : 'cursor-not-allowed',
                )}
                data-testid={`director-model-option-${model.id}`}
              >
                <span className="flex min-w-0 items-center gap-2">
                  <Icon className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
                  <span className="min-w-0">
                    <span className="block truncate text-xs font-medium text-foreground" title={model.label}>{model.label}</span>
                    {model.description && (
                      <span className="block truncate text-xxs text-muted-foreground" title={model.description}>
                        {model.description}
                      </span>
                    )}
                  </span>
                </span>
                <span className="flex flex-shrink-0 items-center gap-1">
                  {!model.enabled && (
                    <span className="rounded bg-warning/10 px-1 py-0.5 text-xxs text-warning">
                      {t('directorModelSection.notConfigured')}
                    </span>
                  )}
                  {isSelected && <Check className="h-3.5 w-3.5 text-primary" aria-hidden="true" />}
                </span>
              </button>

              {canConfigure && (
                <button
                  type="button"
                  onClick={() => handleConfigure(model)}
                  className={cn(
                    'm-1 flex flex-shrink-0 items-center gap-1 rounded-md border border-input-border bg-surface-1 px-2',
                    'text-xs font-medium text-muted-foreground transition-colors hover:border-input-border-hover hover:bg-surface-3 hover:text-foreground',
                    'focus:outline-none focus:ring-2 focus:ring-primary/20 cursor-pointer',
                  )}
                  data-testid={`configure-${model.id}`}
                  title={t('directorModelSection.configure')}
                >
                  <Settings className="h-3.5 w-3.5" aria-hidden="true" />
                  <span>{t('directorModelSection.configure')}</span>
                </button>
              )}
            </div>
          )
        })}
      </div>

      <GatewaySettingsModal
        isOpen={configureModelId !== null && !hasUnresolvedConfig}
        onClose={() => setConfigureModelId(null)}
        onSaved={handleConfigureSaved}
        editConfig={configuringConfig}
        modelKind="director"
        directorPreset={configuringPreset}
        addStartedAnalytics={{
          entryPoint: analyticsEntryPoint,
          configuredModelCount: gatewayConfigs.length,
        }}
      />
    </div>
  )
}
